import { useEffect, useState } from 'react';

import { Alert, HelpTip, Input, Switch } from '@/components/ui';
import { api } from '@/lib/api';

import { formatDataHoraSegundos } from '@/lib/format-date';
/**
 * 🔴 O FREIO DE EMERGÊNCIA DA ESCRITA NO OMIE — e por que ele virou componente.
 *
 * Blindagem 3 (15/08/2026): interruptor NO BANCO, que morde a escrita seguinte
 * na hora, sem reinício. **Só desliga** — soltá-lo não liga nada, porque a
 * chave de ambiente, a lista do que pode subir e o teto de volume continuam
 * mandando.
 *
 * ── ⚠️ Por que ele está num arquivo só, e aparece em dois lugares
 *
 * A reorganização de `Configurações › Integrações` (06/09) deu uma página a
 * cada integração. **Se o freio morasse só na página do Omie, ele ganharia um
 * clique** — e a regra da rodada é explícita: *"é o botão que se procura com
 * pressa; não pode ganhar um nível de navegação."*
 *
 * Então ele fica **na Visão geral E na página do Omie**. 🔴 **Não são dois
 * controles:** é o mesmo componente, lendo e escrevendo o mesmo endpoint. Dois
 * blocos de JSX copiados divergiriam no dia em que alguém mexesse num só — e
 * um freio que mostra estados diferentes em duas telas é pior que freio nenhum.
 */

export interface FreioEscrita {
  bloqueada: boolean;
  motivo: string | null;
  alteradoPor: string | null;
  alteradoEmIso: string | null;
}

function formatIso(iso: string | undefined | null): string {
  if (!iso) return 'nunca';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'nunca';
  return formatDataHoraSegundos(d);
}

const AJUDA_FREIO =
  'Puxado, o freio recusa NA HORA qualquer escrita do sistema no Omie — sem reiniciar nada. ' +
  'Ele só desliga: soltá-lo não liga a escrita, porque a chave de ambiente e as demais travas ' +
  'continuam mandando. A leitura (Omie → sistema) não é afetada. Na dúvida, puxe.';

export function FreioOmie({ compacto = false }: { compacto?: boolean }) {
  const [freio, setFreio] = useState<FreioEscrita | null>(null);
  const [motivo, setMotivo] = useState('');
  const [busy, setBusy] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<FreioEscrita>('/api/v1/admin/omie/freio-escrita')
      .then(setFreio)
      .catch((e: Error) => setErro(e.message));
  }, []);

  async function mudar(bloqueada: boolean) {
    setBusy(true);
    setErro(null);
    try {
      const r = await api.put<FreioEscrita>('/api/v1/admin/omie/freio-escrita', {
        bloqueada,
        motivo: bloqueada ? motivo.trim() || undefined : undefined,
      });
      setFreio(r);
      setMotivo('');
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="rounded-xl border border-line dark:border-line-dark p-3 space-y-2"
      data-testid="omie-freio"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="inline-flex items-center gap-1.5 min-w-0">
          <span className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
            Freio de emergência — bloquear escrita no Omie
          </span>
          <HelpTip texto={AJUDA_FREIO} />
        </span>
        <Switch
          checked={freio?.bloqueada ?? false}
          disabled={busy || !freio}
          onChange={(e) => void mudar(e.target.checked)}
          aria-label="Freio de emergência — bloquear escrita no Omie"
          data-testid="omie-freio-switch"
        />
      </div>

      {erro && (
        <Alert variant="danger" rail>
          Não foi possível falar com o freio: {erro}
        </Alert>
      )}

      {freio?.bloqueada ? (
        <Alert variant="danger" rail title="Freio puxado — nada é escrito no Omie">
          {freio.motivo ? `Motivo: ${freio.motivo}. ` : ''}
          Puxado em {formatIso(freio.alteradoEmIso)}. Toda tentativa de escrita é recusada até
          alguém soltar — e soltar não liga a escrita: as demais travas continuam valendo.
        </Alert>
      ) : (
        !compacto && (
          <>
            <p className="text-xs text-ink-3 dark:text-ink-dark-3">
              Solto. A escrita continua obedecendo as travas de sempre (chave de ambiente, lista do
              que pode subir, teto de volume, piloto cliente a cliente).
            </p>
            <Input
              size="sm"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Motivo (opcional — fica registrado com quem e quando)"
              aria-label="Motivo do freio"
              data-testid="omie-freio-motivo"
            />
          </>
        )
      )}
    </div>
  );
}
